'use client';

import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { Button } from '@/components/ui/button';
import { X, Settings } from 'lucide-react';
import Link from 'next/link';

type ConsentStatus = 'pending' | 'granted' | 'denied' | null;

export function PostHogCookieBanner() {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Vérifier le statut de consentement PostHog
    if (typeof window !== 'undefined' && posthog && posthog.__loaded) {
      const status = posthog.get_explicit_consent_status();
      setConsentStatus(status);

      // Afficher la bannière si le consentement est en attente
      if (status === 'pending' || status === null) {
        setShowBanner(true);
      }
    } else {
      // Si PostHog n'est pas encore chargé, attendre un peu
      const checkInterval = setInterval(() => {
        if (typeof window !== 'undefined' && posthog && posthog.__loaded) {
          const status = posthog.get_explicit_consent_status();
          setConsentStatus(status);
          if (status === 'pending' || status === null) {
            setShowBanner(true);
          }
          clearInterval(checkInterval);
        }
      }, 100);

      return () => clearInterval(checkInterval);
    }
  }, []);

  const handleAccept = () => {
    if (posthog && typeof posthog.opt_in_capturing === 'function') {
      posthog.opt_in_capturing();
      setConsentStatus('granted');
      setShowBanner(false);
    }
  };

  const handleReject = () => {
    if (posthog && typeof posthog.opt_out_capturing === 'function') {
      posthog.opt_out_capturing();
      setConsentStatus('denied');
      setShowBanner(false);
    }
  };

  // Ne pas afficher si le consentement a déjà été donné ou refusé
  if (consentStatus === 'granted' || consentStatus === 'denied') {
    return (
      <button
        onClick={() => setShowSettings(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm text-white shadow-lg transition-all hover:bg-gray-800"
        aria-label="Gérer les cookies"
      >
        <Settings className="h-4 w-4" />
        <span className="hidden sm:inline">Cookies</span>
      </button>
    );
  }

  if (!showBanner && !showSettings) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {!showSettings ? (
          // Bannière principale
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Nous utilisons des cookies
              </h3>
              <p className="text-sm text-gray-600">
                Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic
                du site. Vous pouvez accepter tous les cookies, les refuser ou{' '}
                <button
                  onClick={() => setShowSettings(true)}
                  className="text-[hsl(var(--primary))] underline hover:no-underline"
                >
                  personnaliser vos préférences
                </button>
                .{' '}
                <Link
                  href="/privacy"
                  className="text-[hsl(var(--primary))] underline hover:no-underline"
                >
                  En savoir plus
                </Link>
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button
                onClick={handleReject}
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Refuser
              </Button>
              <Button
                onClick={handleAccept}
                size="sm"
                className="flex-1 sm:flex-none bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
              >
                Tout accepter
              </Button>
            </div>
          </div>
        ) : (
          // Panneau de paramètres
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Gestion des cookies
              </h3>
              <button
                onClick={() => {
                  setShowSettings(false);
                  if (consentStatus === 'pending' || consentStatus === null) {
                    setShowBanner(true);
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Gérez vos préférences de cookies. Les cookies essentiels sont nécessaires au
              fonctionnement du site.{' '}
              <Link
                href="/privacy"
                className="text-[hsl(var(--primary))] underline hover:no-underline"
              >
                En savoir plus
              </Link>
            </p>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                onClick={() => {
                  setShowSettings(false);
                  if (consentStatus === 'pending' || consentStatus === null) {
                    setShowBanner(true);
                  }
                }}
                variant="outline"
                size="sm"
              >
                Annuler
              </Button>
              <Button
                onClick={handleReject}
                variant="outline"
                size="sm"
              >
                Refuser les cookies
              </Button>
              <Button
                onClick={handleAccept}
                size="sm"
                className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
              >
                Accepter les cookies
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

